import React from "react";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/styles";
import { createTheme } from "@material-ui/core";

// Tema do Material UI.
//
// Aqui está TODO o estilo dos componentes `Mui*`, e não em `src/styles/`. O motivo é ordem de
// carga: o Material UI v4 injeta as próprias folhas (JSS) no fim do <head>, depois do CSS da
// aplicação. Com a mesma especificidade, quem chega depois vence — então uma regra de altura de
// input escrita em SCSS perderia para o padrão do MUI, e só voltaria a valer com !important.
// `overrides` entra na mesma folha do MUI, na ordem certa, sem precisar disso.
//
// A divisão é: componente do Material UI se estiliza aqui; Bootstrap, PrimeReact, Metronic e as
// classes do projeto se estilizam em `src/styles/`. Os tokens são os mesmos dos dois lados.
const token = {
    primary: "#354764",
    primaryHover: "#2B3A53",
    primaryActive: "#243247",
    primarySubtle: "rgba(53, 71, 100, 0.08)",
    accent: "#793354",
    danger: "#CF222E",
    success: "#1A7F37",
    warning: "#9A6700",
    info: "#0969DA",
    textPrimary: "#1F2328",
    textSecondary: "#59636E",
    textMuted: "#6E7781",
    border: "#D0D7DE",
    borderStrong: "#B8C0CA",
    surface: "#FFFFFF",
    surfaceSecondary: "#F0F2F5",
    background: "#F6F8FA",
};

const radius = { sm: 6, md: 8, lg: 12, pill: 999 };

const shadow = {
    xs: "0 1px 2px rgba(31, 35, 40, 0.04)",
    sm: "0 2px 6px rgba(31, 35, 40, 0.06)",
    md: "0 8px 24px rgba(31, 35, 40, 0.12)",
    lg: "0 16px 48px rgba(31, 35, 40, 0.16)",
};

const focusRing = "0 0 0 3px rgba(53, 71, 100, 0.18)";
const focusRingInput = "0 0 0 3px rgba(53, 71, 100, 0.12)";
const transition = "160ms ease";

const fontFamily = ["-apple-system", "BlinkMacSystemFont", '"Segoe UI"', '"Helvetica Neue"', "Helvetica", "Arial", "sans-serif"].join(",");

const theme = createTheme(
    /**
     * @see https://material-ui.com/customization/themes/#theme-configuration-variables
     */
    {
        typography: {
            fontFamily,
            fontSize: 14,
            fontWeightMedium: 500,
            fontWeightBold: 600,
            button: {
                fontSize: 14,
                fontWeight: 500,
                textTransform: "none",
            },
        },

        shape: {
            borderRadius: radius.sm,
        },

        palette: {
            contrastThreshold: 2,
            primary: {
                main: token.primary,
                dark: token.primaryActive,
                contrastText: "#ffffff",
            },
            secondary: {
                main: token.accent,
                contrastText: "#ffffff",
            },
            error: { main: token.danger },
            warning: { main: token.warning },
            info: { main: token.info },
            success: { main: token.success },
            text: {
                primary: token.textPrimary,
                secondary: token.textSecondary,
                hint: token.textMuted,
            },
            divider: token.border,
            background: {
                paper: token.surface,
                default: token.background,
            },
        },

        /**
         * @see https://material-ui.com/customization/globals/#default-props
         */
        props: {
            MuiButtonBase: {
                // Sem ripple: a microinteração do sistema é transição de cor, não onda.
                disableRipple: true,
            },

            MuiPopover: { elevation: 1 },

            // Campo contornado é o padrão do sistema. Sem isto, as telas que criam <TextField>
            // sem `variant` cairiam no campo sublinhado e ficariam fora do padrão.
            MuiTextField: { variant: "outlined" },
        },

        overrides: {
            // ==============================================================
            // Superfície
            // ==============================================================
            //
            // A escala de elevação do MUI vira as duas sombras do sistema: nada para o que
            // está na página, sombra só para o que flutua sobre ela.
            MuiPaper: {
                elevation1: { boxShadow: shadow.xs },
                elevation2: { boxShadow: shadow.sm },
                elevation4: { boxShadow: shadow.md },
                elevation8: { boxShadow: shadow.md },
                elevation16: { boxShadow: shadow.lg },
                elevation24: { boxShadow: shadow.lg },
                rounded: { borderRadius: radius.md },
            },

            MuiBackdrop: {
                root: { backgroundColor: "rgba(31, 35, 40, 0.4)" },
            },

            // ==============================================================
            // Campo contornado
            // ==============================================================

            MuiOutlinedInput: {
                root: {
                    backgroundColor: token.surface,
                    borderRadius: radius.sm,
                    transition: `box-shadow ${transition}`,

                    "&:hover:not($disabled):not($error) $notchedOutline": {
                        borderColor: token.borderStrong,
                    },

                    // Foco: borda na cor primária mais anel discreto. O indicador de foco é
                    // requisito de acessibilidade — pode mudar de forma, não desaparecer.
                    "&$focused": {
                        boxShadow: focusRingInput,

                        "& $notchedOutline": {
                            borderColor: token.primary,
                            borderWidth: 1,
                        },
                    },

                    "&$error": {
                        "& $notchedOutline": { borderColor: token.danger },
                        "&$focused": { boxShadow: "0 0 0 3px rgba(207, 34, 46, 0.12)" },
                    },

                    "&$disabled": {
                        backgroundColor: token.surfaceSecondary,

                        "& $notchedOutline": { borderColor: token.border },
                    },
                },

                // O vão da label começa em borda (1) + padding + folga do vão (5), e precisa bater
                // com o `translate` X da label flutuante (MuiInputLabel abaixo): 1 + 10 + 5 = 16.
                // Os dois mudam juntos. Com o padrão do MUI (8px) e o canto de 6px de raio, o vão
                // começava quase em cima da curva, e a label parecia esbarrar no canto.
                notchedOutline: {
                    padding: "0 10px",
                    borderColor: token.border,
                    borderWidth: 1,
                    transition: `border-color ${transition}`,
                },

                // 36px de altura: 20 de conteúdo + 8 de folga em cima e embaixo.
                input: {
                    height: 20,
                    padding: "8px 12px",
                    color: token.textPrimary,
                    fontSize: 14,

                    "&::placeholder": {
                        color: token.textMuted,
                        opacity: 1,
                    },
                },

                multiline: { padding: "8px 12px" },
                inputMultiline: { padding: 0, lineHeight: 1.5 },
                adornedEnd: { paddingRight: 8 },
            },

            // A label flutuante acompanha a altura nova: centrada no campo em repouso, e em
            // cima da borda quando há valor.
            //
            // Duas medidas aqui não são livres. O deslocamento vertical de -5px é metade da
            // altura da label já reduzida (14px x 0.75), e é o que faz a borda passar pelo meio
            // do texto em vez de por baixo dele. E a label não pode ficar em negrito: o Material
            // UI abre o vão na borda medindo o mesmo texto no peso normal, então um peso maior
            // fica mais largo que o vão e o traço da borda encosta na última letra.
            MuiInputLabel: {
                outlined: {
                    transform: "translate(12px, 9px) scale(1)",

                    // Em repouso a label fica a 12px, alinhada ao texto do input; flutuando, o texto
                    // fica a 16px, dentro do vão da borda (ver `notchedOutline`).
                    //
                    // O vão é aberto só na borda: o anel de foco (box-shadow do OutlinedInput)
                    // passava inteiro por trás da label. O fundo da metade de cima, a que fica fora
                    // do campo, cobre o anel na largura do vão inteiro. A metade de baixo fica
                    // transparente para não pintar de branco o fundo cinza de um campo desabilitado.
                    //
                    // O padding dá essa largura: 6.67px x 0.75 = os 5px de folga do vão. O
                    // `translate` desconta os mesmos 5px para o texto continuar em 16.
                    "&$shrink": {
                        transform: "translate(11px, -5px) scale(0.75)",
                        padding: "0 6.67px",
                        background: `linear-gradient(to bottom, ${token.surface} 50%, transparent 50%)`,
                    },
                },
            },

            MuiFormLabel: {
                root: {
                    color: token.textSecondary,
                    fontSize: 14,

                    "&$focused": { color: token.primary },
                    "&$error": { color: token.danger },
                },
                asterisk: {
                    "&$error": { color: token.danger },
                },
            },

            // O texto de apoio destes formulários explica o efeito do campo no orçamento.
            // É informação, não letra miúda decorativa.
            MuiFormHelperText: {
                root: {
                    color: token.textMuted,
                    fontSize: 12,
                    lineHeight: 1.5,
                    marginTop: 4,

                    "&$error": { color: token.danger },
                },
                contained: {
                    marginLeft: 0,
                    marginRight: 0,
                },
            },

            MuiFormControl: {
                root: { width: "100%" },
                // 16px entre campos: campo colado em campo é o defeito mais visível de um
                // formulário longo.
                marginNormal: {
                    marginTop: 0,
                    marginBottom: 16,
                },
            },

            MuiInputBase: {
                root: { fontSize: 14 },
                input: {
                    color: token.textPrimary,

                    "&$disabled": { color: token.textSecondary },
                },
            },

            // Campo sublinhado, ainda usado em telas antigas
            MuiInput: {
                underline: {
                    "&:before": { borderBottomColor: token.border },
                    "&:hover:not($disabled):before": { borderBottomColor: token.borderStrong },
                    "&:after": { borderBottomColor: token.primary },
                },
            },

            MuiSelect: {
                outlined: { paddingRight: 32 },
                icon: {
                    color: token.textMuted,
                    right: 8,
                },
                select: {
                    "&:focus": {
                        backgroundColor: "transparent",
                        borderRadius: radius.sm,
                    },
                },
            },

            // ==============================================================
            // Menu, lista e autocomplete
            // ==============================================================

            MuiMenu: {
                paper: {
                    border: `1px solid ${token.border}`,
                    borderRadius: radius.md,
                    boxShadow: shadow.md,
                },
            },

            MuiMenuItem: {
                root: {
                    color: token.textPrimary,
                    fontSize: 14,
                    minHeight: 34,

                    "&:hover": { backgroundColor: token.background },

                    "&$selected": {
                        backgroundColor: token.primarySubtle,
                        color: token.primary,
                        fontWeight: 500,

                        "&:hover": { backgroundColor: "rgba(53, 71, 100, 0.12)" },
                    },
                },
            },

            MuiListItem: {
                root: {
                    "&$selected": {
                        backgroundColor: token.primarySubtle,
                        color: token.primary,
                    },
                },
            },

            // ==============================================================
            // Seleção
            // ==============================================================

            MuiCheckbox: {
                root: {
                    color: token.borderStrong,
                    padding: 8,

                    "&$disabled": { color: token.border },
                },
                colorPrimary: { "&$checked": { color: token.primary } },
                colorSecondary: { "&$checked": { color: token.primary } },
            },

            MuiRadio: {
                root: {
                    color: token.borderStrong,
                    padding: 8,
                },
                colorPrimary: { "&$checked": { color: token.primary } },
                colorSecondary: { "&$checked": { color: token.primary } },
            },

            MuiSwitch: {
                colorPrimary: {
                    "&$checked": {
                        color: token.primary,

                        "& + $track": { backgroundColor: token.primary },
                    },
                },
                colorSecondary: {
                    "&$checked": {
                        color: token.primary,

                        "& + $track": { backgroundColor: token.primary },
                    },
                },
            },

            MuiFormControlLabel: {
                label: {
                    color: token.textPrimary,
                    fontSize: 14,
                },
            },

            // ==============================================================
            // Botões
            // ==============================================================

            MuiButton: {
                root: {
                    minHeight: 32,
                    borderRadius: radius.sm,
                    padding: "0 12px",
                },
                contained: {
                    boxShadow: "none",
                    "&:hover, &:focus, &:active": { boxShadow: "none" },
                },
                containedPrimary: {
                    "&:hover": { backgroundColor: token.primaryHover },
                    "&:active": { backgroundColor: token.primaryActive },
                },
                outlined: {
                    borderColor: token.border,
                    color: token.primary,

                    "&:hover": {
                        backgroundColor: token.background,
                        borderColor: token.borderStrong,
                    },
                },
            },

            // O tema apagava por completo o retorno visual do ícone-botão; a área de clique
            // precisa reagir para quem usa teclado e para quem usa mouse.
            MuiIconButton: {
                root: {
                    borderRadius: radius.sm,
                    color: token.textSecondary,
                    padding: 8,

                    "&:hover": { backgroundColor: token.surfaceSecondary },
                    "&:focus-visible": { boxShadow: focusRing },
                },
            },

            // ==============================================================
            // Abas, diálogo e tooltip
            // ==============================================================

            MuiTabs: {
                root: {
                    borderBottom: `1px solid ${token.border}`,
                    minHeight: 40,
                },
                indicator: {
                    backgroundColor: token.primary,
                    height: 2,
                },
            },

            MuiTab: {
                root: {
                    color: token.textSecondary,
                    fontSize: 14,
                    fontWeight: 500,
                    minHeight: 40,
                    textTransform: "none",

                    "&$selected": {
                        color: token.primary,
                        fontWeight: 600,
                    },
                },
            },

            MuiDialog: {
                paper: {
                    border: `1px solid ${token.border}`,
                    borderRadius: radius.lg,
                    boxShadow: shadow.lg,
                },
            },

            MuiDialogTitle: {
                root: {
                    borderBottom: `1px solid ${token.border}`,
                    padding: "20px 24px",
                },
            },

            MuiDialogContent: {
                root: { padding: 24 },
            },

            // A ação principal do diálogo fica à direita.
            MuiDialogActions: {
                root: {
                    borderTop: `1px solid ${token.border}`,
                    justifyContent: "flex-end",
                    padding: "16px 24px",
                },
                spacing: {
                    "& > :not(:first-child)": { marginLeft: 8 },
                },
            },

            MuiTooltip: {
                tooltip: {
                    backgroundColor: token.textPrimary,
                    borderRadius: radius.sm,
                    fontSize: 12,
                    fontWeight: 400,
                    padding: "8px 12px",
                },
                arrow: { color: token.textPrimary },
            },

            MuiCircularProgress: {
                colorPrimary: { color: token.primary },
            },

            MuiLinearProgress: {
                colorPrimary: { backgroundColor: token.primarySubtle },
                barColorPrimary: { backgroundColor: token.primary },
            },

            MuiChip: {
                root: {
                    backgroundColor: token.surfaceSecondary,
                    borderRadius: radius.pill,
                    color: token.textPrimary,
                    fontSize: 12,
                    fontWeight: 500,
                    height: 24,
                },
            },
        },
    },
);

export default function ThemeProvider(props) {
    const { children } = props;

    return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
}
